import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import useHideChatbot from "@/hooks/useHideChatbot";

export default function AdminLogin() {
  useHideChatbot();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back, Dom.");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="admin-login-page">
      <header className="border-b border-black">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading font-black text-lg tracking-tight" data-testid="login-brand-link">
            DOM<span className="text-[#FF4500]">.</span>LAFRANCE
          </Link>
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.18em] hover:text-[#FF4500]" data-testid="login-back-link">
            ← Back to site
          </Link>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 border-b border-black">
        <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white border-r border-black">
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60">
            ADMIN · ACCESS RESTRICTED
          </div>
          <div>
            <div className="font-heading font-black text-6xl tracking-tighter leading-[0.9]">
              CONTROL<br />ROOM<span className="text-[#FF4500]">.</span>
            </div>
            <p className="mt-6 text-white/60 max-w-md">
              Publish market notes, money hacks, and review every contact + newsletter
              submission.
            </p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">
            v1.0 · Experior Financial
          </div>
        </div>

        <div className="flex items-center justify-center p-8 md:p-16">
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-8" data-testid="admin-login-form">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-black/60">Sign in</div>
              <h1 className="font-heading font-black text-5xl tracking-tighter leading-none mt-3">
                Admin login.
              </h1>
            </div>

            <div>
              <label className="label-eyebrow">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="dom-input mt-3"
                placeholder="dlafrance@experiorfinancial.ca"
                required
                autoFocus
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-eyebrow">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="dom-input mt-3"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="border border-[#FF3B30] text-[#FF3B30] px-4 py-3 font-mono text-xs uppercase tracking-[0.15em]" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-btn"
            >
              {loading ? "Authenticating…" : "Enter →"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
